import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { FiRefreshCw } from 'react-icons/fi';
import { useAnalyticsReports } from '../hooks/useAnalyticsReports';
import '../styles/reports.scss';

const TOPICS = [
    { key: 'users', label: 'المستخدمون' },
    { key: 'drivers', label: 'السائقون' },
    { key: 'trips', label: 'الطلبات والرحلات' },
];

const TIME_FRAMES = [
    { key: 'daily', label: 'يومي' },
    { key: 'weekly', label: 'أسبوعي' },
    { key: 'monthly', label: 'شهري' },
];

const CHART_COLORS = ['#1d4ed8', '#0ea5e9', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6'];

const formatValue = (value, type = 'number') => {
    const numeric = Number(value || 0);
    if (type === 'percent') return `${numeric.toFixed(2)}%`;
    if (type === 'currency') return `${numeric.toFixed(2)} د.ل`;
    return numeric.toLocaleString('ar-LY');
};

const getDefaultRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 29);

    const toInput = (date) => {
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    return {
        startDate: toInput(start),
        endDate: toInput(end),
    };
};

const buildCards = (topic, metrics) => {
    if (!metrics) return [];

    if (topic === 'users') {
        return [
            { title: 'إجمالي المستخدمين الحاليين', value: formatValue(metrics.totalUsers) },
            { title: 'نسبة النمو مقارنة بالفترة السابقة', value: formatValue(metrics.growthPercent, 'percent'), growth: metrics.growthPercent },
            { title: 'متوسط الانضمام', value: formatValue(metrics.averageJoiningPerFrame) },
            { title: 'المستخدمون الذين لديهم رحلات', value: formatValue(metrics.usersWithTrips) },
            { title: 'المستخدمون الجدد في الفترة الحالية', value: formatValue(metrics.newUsers) },
            { title: 'نسبة إستعمال المستخدمين الجدد للتطبيق', value: formatValue(metrics.joinedUsersWithTripsPercent, 'percent') },
            { title: 'المستخدمون الجدد في الفترة السابقة', value: formatValue(metrics.previousNewUsers) },
        ];
    }

    if (topic === 'drivers') {
        return [
            { title: 'إجمالي السائقين الحاليين', value: formatValue(metrics.totalDrivers) },
            { title: 'نسبة النمو مقارنة بالفترة السابقة', value: formatValue(metrics.growthPercent, 'percent'), growth: metrics.growthPercent },
            { title: 'متوسط الانضمام', value: formatValue(metrics.averageJoiningPerFrame) },
            { title: 'السائقون الذين لديهم رحلات', value: formatValue(metrics.driversWithTrips) },
            { title: 'السائقون النشطون حالياً', value: formatValue(metrics.activeDrivers) },
            { title: 'متوسط عدد رحلات جميع السائقين النشطين', value: formatValue(metrics.averageTripsForActiveDrivers) },
            { title: 'متوسط رحلات السائقين النشطين (أكثر من 0)', value: formatValue(metrics.averageTripsForActiveDriversWithTrips) },
            { title: 'السائقون الجدد في الفترة المحددة', value: formatValue(metrics.newDrivers) },
            { title: ' عدد طلبات تسجيل السائقين في الفترة المحددة', value: formatValue(metrics.towRequestsCount) },
        ];
    }

    return [
        { title: 'إجمالي طلبات المستخدمين', value: formatValue(metrics.totalUserRequests) },
        { title: 'متوسط الطلبات', value: formatValue(metrics.averageRequestsPerFrame) },
        { title: 'نسبة النمو مقارنة بالفترة السابقة', value: formatValue(metrics.growthPercent, 'percent'), growth: metrics.growthPercent },
        { title: 'متوسط السعر المبدئي للطلب', value: formatValue(metrics.averageRequestStartPrice, 'currency') },
        { title: 'متوسط عدد العروض لكل طلب', value: formatValue(metrics.averageOffersPerRequest) },
        { title: 'متوسط سعر العرض', value: formatValue(metrics.averageOfferPrice, 'currency') },
        { title: 'احتمال تحول الطلب إلى رحلة', value: formatValue(metrics.requestToTripLikelihood, 'percent') },
        { title: 'احتمال اكتمال الرحلة', value: formatValue(metrics.tripCompletionLikelihood, 'percent') },
        { title: 'احتمال إلغاء/فشل الرحلة', value: formatValue(metrics.tripCancellationLikelihood, 'percent') },
        { title: 'احتمال تحول الطلب إلى رحلة مكتملة', value: formatValue(metrics.requestToCompletedTripLikelihood, 'percent') },
        { title: 'متوسط قيمة الرحلة', value: formatValue(metrics.averageTripCost, 'currency') },
    ];
};

export const Reports = () => {
    const user = useSelector((state) => state.userController.user);
    const navigate = useNavigate();
    const { report, isLoading, fetchAnalytics } = useAnalyticsReports();

    const [topic, setTopic] = useState('users');
    const [timeframe, setTimeframe] = useState('daily');
    const [range, setRange] = useState(getDefaultRange());

    useEffect(() => {
        if (!user || !user.token) {
            navigate('/login');
        }
    }, [user, navigate]);

    const loadData = useCallback(async () => {
        await fetchAnalytics({
            topic,
            timeframe,
            startDate: range.startDate,
            endDate: range.endDate,
        });
    }, [fetchAnalytics, topic, timeframe, range.startDate, range.endDate]);

    useEffect(() => {
        if (user?.token) {
            loadData();
        }
    }, [user?.token, loadData]);

    const cards = useMemo(() => buildCards(topic, report?.data?.metrics), [topic, report]);

    const usersCharts = (
        <>
            <div className="chart-card large">
                <h3>  المستخدمين الجدد</h3>
                <ResponsiveContainer width="100%" height={290}>
                    <LineChart data={report?.data?.charts?.joinsTimeline || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="joinedUsers" stroke="#1d4ed8" strokeWidth={2} name="مستخدمون جدد" />
                    </LineChart>
                </ResponsiveContainer>
            </div>
            <div className="chart-card">
                <h3>معدل استخدام التطبيق</h3>
                <ResponsiveContainer width="100%" height={290}>
                    <PieChart>
                        <Pie
                            data={report?.data?.charts?.engagementBreakdown || []}
                            dataKey="value"
                            nameKey="name"
                            outerRadius={95}
                            label
                        >
                            {(report?.data?.charts?.engagementBreakdown || []).map((entry, index) => (
                                <Cell key={`${entry.name}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        </>
    );

    const driversCharts = (
        <>
            <div className="chart-card large">
                <h3>  السائقين الجدد</h3>
                <ResponsiveContainer width="100%" height={290}>
                    <LineChart data={report?.data?.charts?.joinsTimeline || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="joinedDrivers" stroke="#0ea5e9" strokeWidth={2} name="سائقون جدد" />
                    </LineChart>
                </ResponsiveContainer>
            </div>
            <div className="chart-card large">
                <h3>حالة طلبات تسجيل السائقين</h3>
                <ResponsiveContainer width="100%" height={290}>
                    <BarChart data={report?.data?.charts?.towRequestsTimeline || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="pending" stackId="state" fill="#f59e0b" name="معلق" />
                        <Bar dataKey="approved" stackId="state" fill="#22c55e" name="مقبول" />
                        <Bar dataKey="rejected" stackId="state" fill="#ef4444" name="مرفوض" />
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <div className="chart-card">
                <h3>توزيع نشاط السائقين</h3>
                <ResponsiveContainer width="100%" height={290}>
                    <PieChart>
                        <Pie
                            data={report?.data?.charts?.activityBreakdown || []}
                            dataKey="value"
                            nameKey="name"
                            outerRadius={95}
                            label
                        >
                            {(report?.data?.charts?.activityBreakdown || []).map((entry, index) => (
                                <Cell key={`${entry.name}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        </>
    );

    const tripsCharts = (
        <>
            <div className="chart-card large">
                <h3>الطلبات والعروض والرحلات عبر الزمن</h3>
                <ResponsiveContainer width="100%" height={310}>
                    <LineChart data={report?.data?.charts?.timeline || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="requests" stroke="#1d4ed8" strokeWidth={2} name="الطلبات" />
                        <Line type="monotone" dataKey="offers" stroke="#f59e0b" strokeWidth={2} name="العروض" />
                        <Line type="monotone" dataKey="completedTrips" stroke="#22c55e" strokeWidth={2} name="رحلات مكتملة" />
                    </LineChart>
                </ResponsiveContainer>
            </div>
            <div className="chart-card">
                <h3>أنواع الأعطال المطلوبة</h3>
                <ResponsiveContainer width="100%" height={290}>
                    <BarChart data={report?.data?.charts?.problemTypeBreakdown || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="value" fill="#0ea5e9" name="عدد الطلبات" />
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <div className="chart-card">
                <h3>قمع التحويل من الطلب إلى رحلة مكتملة</h3>
                <ResponsiveContainer width="100%" height={290}>
                    <BarChart data={report?.data?.charts?.funnel || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="value" fill="#1d4ed8" name="عدد" />
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <div className="chart-card">
                <h3>مخرجات الرحلات</h3>
                <ResponsiveContainer width="100%" height={290}>
                    <PieChart>
                        <Pie
                            data={report?.data?.charts?.tripOutcome || []}
                            dataKey="value"
                            nameKey="name"
                            outerRadius={95}
                            label
                        >
                            {(report?.data?.charts?.tripOutcome || []).map((entry, index) => (
                                <Cell key={`${entry.name}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        </>
    );

    return (
        <section className="reports-analytics-page">
            <div className="reports-header">
                <div className="tabs">
                    {TOPICS.map((item) => (
                        <button
                            key={item.key}
                            className={`tab-btn ${topic === item.key ? 'active' : ''}`}
                            onClick={() => setTopic(item.key)}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>

                <div className="filters">
                    <div className="field">
                        <label>من</label>
                        <input
                            type="date"
                            value={range.startDate}
                            onChange={(e) => setRange((prev) => ({ ...prev, startDate: e.target.value }))}
                        />
                    </div>
                    <div className="field">
                        <label>إلى</label>
                        <input
                            type="date"
                            value={range.endDate}
                            onChange={(e) => setRange((prev) => ({ ...prev, endDate: e.target.value }))}
                        />
                    </div>
                    <div className="field">
                        <label>الإطار الزمني</label>
                        <select value={timeframe} onChange={(e) => setTimeframe(e.target.value)}>
                            {TIME_FRAMES.map((item) => (
                                <option key={item.key} value={item.key}>{item.label}</option>
                            ))}
                        </select>
                    </div>
                    <button className="refresh-btn" onClick={loadData} disabled={isLoading}>
                        <FiRefreshCw />
                        تحديث
                    </button>
                </div>
            </div>

            {isLoading && (
                <div className="loading-state">جاري تحميل الإحصائيات...</div>
            )}

            {!isLoading && (
                <>
                    <div className="cards-grid">
                        {cards.map((card, index) => (
                            <div key={`${card.title}-${index}`} className="metric-card">
                                <p className="title">{card.title}</p>
                                <h3>{card.value}</h3>
                                {typeof card.growth === 'number' && (
                                    <span className={`growth ${card.growth >= 0 ? 'up' : 'down'}`}>
                                        {card.growth >= 0 ? '+' : ''}{card.growth.toFixed(2)}%
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="charts-grid">
                        {topic === 'users' && usersCharts}
                        {topic === 'drivers' && driversCharts}
                        {topic === 'trips' && tripsCharts}
                    </div>
                </>
            )}
        </section>
    );
};

export default Reports;